import { useIntl } from 'react-intl';
import { COMPLETE } from './completeUnit';
import packBreadcrumb from './packBreadcrumb';
import { RELEASE } from './releaseUnit';
import { chain } from './story';
import PassportStoryPage from './PassportStoryPage';
import { partLoaders } from './lazyParts';
import StoryVignette, { useStory } from './StoryVignette';
import useHeroData from './useHeroData';

/*
 * Home's hero, live: the Kestrel pack unit's passport in the app, completed
 * (its rated capacity and its safety instructions, English and German, typed
 * into the value drawer) and released, in about 12 seconds (about 13.5 a
 * loop, its rest and the dip back included), then again. The phone beside
 * it (PublicPassportVignette) is the public page, following the same story:
 * each saved value lands on it, it turns Released and a reader switches it
 * to German. (Composing the public page is the Platform page's Compose
 * step, not this story: ask 2.)
 *
 *   const story = useHeroStory();
 *   <HeroStory story={story} />            // the window
 *   <PublicPassportVignette story={story} /> // the phone
 *
 * Under reduced motion the window rests on the released passport and the
 * phone on the released public page, in German, at the safety instructions.
 */

/** Complete, then release: the hero's story. */
export const HERO_STORY = chain([COMPLETE, RELEASE]);

/**
 * The hero's pack's passport at each point of its story (see
 * fixtures/kestrelHero.js). A story about the same pack elsewhere (the
 * Platform page's) reaches it through here, so it loads the hero's modules
 * whole rather than a part of them (which would split Home's chunk of them).
 */
export { heroPassports } from './fixtures/kestrelHero';

/**
 * The hero's data and its pack's passport page, for a story that ends on
 * that page elsewhere: reached through here, for the same reason as
 * heroPassports.
 */
export { default as PassportStoryPage } from './PassportStoryPage';
export { default as useHeroData } from './useHeroData';

/** The Composer page's trail (Composer/Edit.jsx: Passports, Current SKUs, the ontology). */
const composerTrail = (hero) => ['Passports', 'Current SKUs', hero.ontology.name];

/**
 * The window's height, in layout px at its base width: the passport's header,
 * tabs and the values down to the Safety group (the two rows the story fills)
 * whole, beside the summary rail.
 */
export const HERO_HEIGHT = { 1200: 790 };

/**
 * Where, on the window laid out 1200px wide, the phone beside it may go
 * (layout px from its top left): centred on the summary rail (x), its top
 * edge (y) in the only band that cuts no line of the rail's text both in
 * the draft (between its Status and Schema rows) and after the release
 * (between its completeness and status cards, which the lock banner has
 * moved down). The phone is as wide as the rail's text column plus a little
 * of the cards' padding, so its sides cut no text either. Measured in the
 * browser; see the Task 4e report.
 */
export const HERO_PHONE_ANCHOR = Object.freeze({ x: 1044, y: 401 });

/**
 * The hero's parts (see lazyParts), loaded through heroParts.js: the passport
 * page's cues, the drawer's editors, the phone beside the window (Hero.jsx
 * asks for it), and the Composer (the Platform page's Compose step plays on
 * these pages; Home's story never asks for it).
 */
export const HERO_PART_LOADERS = partLoaders(() => import('./heroParts'), ['passportCues', 'drawer', 'composer', 'phone']);

/**
 * The pages the hero's units play on (see useStory): the passport, in the
 * page's first load, with its cues a part; the Composer, a part (its module
 * carries its cues), for a story that composes (the Platform page's Compose
 * step). A page no beat shows is never mounted or asked for. The drawer's
 * editors are a part of their own (the "complete" unit's `needs`).
 */
export const HERO_PAGES = Object.freeze({
    passport: { Component: PassportStoryPage, cues: HERO_PART_LOADERS.passportCues },
    composer: { part: HERO_PART_LOADERS.composer },
});

const HERO_PARTS = Object.freeze({ drawer: HERO_PART_LOADERS.drawer });

/**
 * The hero's story state, to share between the window and the phone.
 * `story` replaces the whole story with a chain of its units (e.g.
 * `chain([COMPOSE], { settled: [COMPLETE, RELEASE] })`); `active: false`
 * holds it and `loop: false` plays it once (see useStory); a parent that
 * needs it back at its opening remounts the component that calls this (a
 * new `key`).
 */
export function useHeroStory({ story = HERO_STORY, active = true, loop } = {}) {
    // A story rests where it ends under reduced motion (its last beat's
    // page: the passport for the hero, the Composer for compose alone).
    return useStory(story, { pages: HERO_PAGES, parts: HERO_PARTS, active, loop });
}

/**
 * The window. `story` is useHeroStory()'s; `baseWidth` the width the app is
 * laid out at before being fitted to the frame (1200; the same on a phone:
 * a stage draws the desktop app at every screen width, VignetteFrame);
 * `height` replaces the window's height at that width (the Platform
 * stepper's window, one size for every step it shows).
 */
export default function HeroStory({ story, baseWidth = 1200, height, label, className }) {
    const { formatMessage } = useIntl();
    const hero = useHeroData();

    return (
        <StoryVignette
            state={story}
            hero={hero}
            trails={{ passport: packBreadcrumb(hero.kestrel), composer: composerTrail(hero) }}
            baseWidth={baseWidth}
            height={height ?? HERO_HEIGHT[baseWidth] ?? HERO_HEIGHT[1200]}
            label={label ?? formatMessage({ id: 'site.vignette.passportOverview' })}
            className={className}
        />
    );
}
